/**
 * RightsChip — visible origin/rights label on every rendered creative so
 * clients always know what's safe to ship:
 *    original  — client's own photo / approved upload (safe)
 *    generated — AI-generated artwork (requires client sign-off)
 *    licensed  — fetched from a licensed stock library (keep attribution)
 *
 * Clickable when `onClick` is provided — opens the RightsAttachModal.
 * When `attached` is truthy, chip shows a verified ✓ instead of the "+".
 */
import { Image, Sparkles, FileCheck2, Check } from "lucide-react";

const CONFIG = {
  original:  { label: "Original photo",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Image },
  generated: { label: "AI-generated",     cls: "bg-violet-50 text-violet-700 border-violet-200",   Icon: Sparkles },
  licensed:  { label: "Licensed stock",   cls: "bg-sky-50 text-sky-700 border-sky-200",            Icon: FileCheck2 },
};

export default function RightsChip({ origin = "generated", size = "sm", className = "", onClick, attached = false }) {
  const c = CONFIG[origin] || CONFIG.generated;
  const base =
    size === "sm"
      ? "text-[10px] px-1.5 py-0.5"
      : "text-xs px-2 py-1";
  const isInteractive = typeof onClick === "function";
  // "original" images don't typically need external paperwork, so the
  // modal is only interactive for generated/licensed.
  const showAttach = isInteractive && origin !== "original";
  return (
    <button
      type="button"
      disabled={!showAttach}
      onClick={showAttach ? onClick : undefined}
      data-testid={`rights-chip-${origin}`}
      className={`inline-flex items-center gap-1 border rounded-full font-bold uppercase tracking-wider ${c.cls} ${base} ${showAttach ? "cursor-pointer hover:ring-2 hover:ring-current/40 transition" : "cursor-default"} ${className}`}
      title={showAttach
        ? `${c.label} — click to attach licence or release doc`
        : `${c.label} — attached to this creative for rights tracking`}
    >
      <c.Icon size={size === "sm" ? 10 : 12} />
      {c.label}
      {showAttach && attached && <Check size={10} className="ml-0.5" />}
      {showAttach && !attached && <span className="ml-0.5 opacity-60">+</span>}
    </button>
  );
}
